import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
    User, Mail, Building, Phone, Shield, Bell, Lock, Trash2,
    ArrowLeft, Save, RefreshCw, CheckCircle, AlertTriangle
} from 'lucide-react';
import { profileAPI } from '@/lib/api';
import { LOGO_URL } from '@/lib/brand';

const Profile = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    
    // Form states
    const [fullName, setFullName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [phone, setPhone] = useState('');
    const [notifications, setNotifications] = useState({
        email_alerts: true,
        sms_alerts: false,
        voice_alerts: false,
        weekly_report: true
    });
    
    // Password change states
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    // Delete account states
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        setIsLoading(true);
        try {
            const response = await profileAPI.get();
            setProfile(response.data);
            setFullName(response.data.full_name);
            setCompanyName(response.data.company_name);
            setPhone(response.data.phone || '');
            setNotifications(response.data.notification_preferences || {
                email_alerts: true,
                sms_alerts: false,
                voice_alerts: false,
                weekly_report: true
            });
        } catch (error) {
            console.error('Error loading profile:', error);
            setMessage({ type: 'error', text: 'Failed to load profile' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveProfile = async () => {
        setIsSaving(true);
        setMessage({ type: '', text: '' });
        try {
            await profileAPI.update({
                full_name: fullName,
                company_name: companyName,
                phone: phone || null,
                notification_preferences: notifications
            });
            
            // Update local storage
            const userData = JSON.parse(localStorage.getItem('shirosec_user') || '{}');
            userData.full_name = fullName;
            userData.company_name = companyName;
            localStorage.setItem('shirosec_user', JSON.stringify(userData));
            
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
            await loadProfile();
        } catch (error) {
            console.error('Error updating profile:', error);
            setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update profile' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleChangePassword = async () => {
        if (newPassword !== confirmPassword) {
            setMessage({ type: 'error', text: 'Passwords do not match' });
            return;
        }
        
        if (newPassword.length < 8) {
            setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
            return;
        }
        
        setIsSaving(true);
        setMessage({ type: '', text: '' });
        try {
            await profileAPI.changePassword({
                current_password: currentPassword,
                new_password: newPassword
            });
            setMessage({ type: 'success', text: 'Password changed successfully!' });
            setShowPasswordForm(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            console.error('Error changing password:', error);
            setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to change password' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteAccount = async () => {
        try {
            await profileAPI.deleteAccount();
            localStorage.removeItem('shirosec_token');
            localStorage.removeItem('shirosec_user');
            navigate('/');
        } catch (error) {
            console.error('Error deleting account:', error);
            setMessage({ type: 'error', text: 'Failed to delete account' });
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-white">
            {/* Header */}
            <header className="border-b border-white/5 bg-[#0d0d14]">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white">
                            <ArrowLeft className="w-5 h-5" />
                            Back to Dashboard
                        </Link>
                    </div>
                    <Link to="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Shiro Security" className="w-8 h-8 object-contain" />
                        <span className="text-lg font-bold gradient-text">SHIRO</span>
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2">Profile Settings</h1>
                    <p className="text-white/60">Manage your account settings and preferences</p>
                </div>

                {/* Message Alert */}
                {message.text && (
                    <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                        message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                        {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                        {message.text}
                    </div>
                )}

                <div className="space-y-6">
                    {/* Account Overview */}
                    <Card className="glass-card border-0">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="w-5 h-5 text-cyan-400" />
                                Account Overview
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="text-center p-4 bg-white/5 rounded-xl">
                                    <div className="text-3xl font-bold text-cyan-400">{profile?.devices_count || 0}</div>
                                    <div className="text-sm text-white/60">Devices Protected</div>
                                </div>
                                <div className="text-center p-4 bg-white/5 rounded-xl">
                                    <div className="text-3xl font-bold text-green-400">{profile?.threats_blocked || 0}</div>
                                    <div className="text-sm text-white/60">Threats Blocked</div>
                                </div>
                                <div className="text-center p-4 bg-white/5 rounded-xl">
                                    <Badge className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30 text-lg px-4 py-2">
                                        {profile?.plan?.toUpperCase() || 'FREE'}
                                    </Badge>
                                    <div className="text-sm text-white/60 mt-2">Current Plan</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Personal Information */}
                    <Card className="glass-card border-0">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="w-5 h-5 text-cyan-400" />
                                Personal Information
                            </CardTitle>
                            <CardDescription>Update your personal details</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="fullName">Full Name</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                        <Input
                                            id="fullName"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            className="pl-10 bg-white/5 border-white/10 text-white"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="companyName">Company Name</Label>
                                    <div className="relative">
                                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                        <Input
                                            id="companyName"
                                            value={companyName}
                                            onChange={(e) => setCompanyName(e.target.value)}
                                            className="pl-10 bg-white/5 border-white/10 text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email Address</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                        <Input
                                            id="email"
                                            value={profile?.email || ''}
                                            disabled
                                            className="pl-10 bg-white/5 border-white/10 text-white/60"
                                        />
                                    </div>
                                    <p className="text-xs text-white/40">Email cannot be changed</p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone Number (Optional)</Label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                        <Input
                                            id="phone"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder="+1 (555) 000-0000"
                                            className="pl-10 bg-white/5 border-white/10 text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Notification Preferences */}
                    <Card className="glass-card border-0">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Bell className="w-5 h-5 text-cyan-400" />
                                Notification Preferences
                            </CardTitle>
                            <CardDescription>Choose how you want to receive alerts</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                                <div>
                                    <div className="font-medium">Email Alerts</div>
                                    <div className="text-sm text-white/60">Receive threat alerts via email</div>
                                </div>
                                <Switch
                                    checked={notifications.email_alerts}
                                    onCheckedChange={(checked) => setNotifications({...notifications, email_alerts: checked})}
                                />
                            </div>
                            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                                <div>
                                    <div className="font-medium">SMS Alerts</div>
                                    <div className="text-sm text-white/60">Receive urgent alerts via SMS</div>
                                    <Badge className="mt-1 bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">Coming Soon</Badge>
                                </div>
                                <Switch
                                    checked={notifications.sms_alerts}
                                    onCheckedChange={(checked) => setNotifications({...notifications, sms_alerts: checked})}
                                    disabled
                                />
                            </div>
                            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                                <div>
                                    <div className="font-medium">Voice Call Alerts</div>
                                    <div className="text-sm text-white/60">Receive critical alerts via phone call</div>
                                    <Badge className="mt-1 bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">Coming Soon</Badge>
                                </div>
                                <Switch
                                    checked={notifications.voice_alerts}
                                    onCheckedChange={(checked) => setNotifications({...notifications, voice_alerts: checked})}
                                    disabled
                                />
                            </div>
                            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                                <div>
                                    <div className="font-medium">Weekly Security Report</div>
                                    <div className="text-sm text-white/60">Receive a weekly summary of your security status</div>
                                </div>
                                <Switch
                                    checked={notifications.weekly_report}
                                    onCheckedChange={(checked) => setNotifications({...notifications, weekly_report: checked})}
                                />
                            </div>
                        </CardContent>
                    </Card>

                    {/* Save Button */}
                    <Button
                        onClick={handleSaveProfile}
                        disabled={isSaving}
                        className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700"
                    >
                        {isSaving ? (
                            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                        ) : (
                            <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                        )}
                    </Button>

                    {/* Security Section */}
                    <Card className="glass-card border-0">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Lock className="w-5 h-5 text-cyan-400" />
                                Security
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {!showPasswordForm ? (
                                <Button
                                    variant="outline"
                                    onClick={() => setShowPasswordForm(true)}
                                    className="w-full border-white/10 text-white hover:bg-white/5"
                                >
                                    <Lock className="w-4 h-4 mr-2" /> Change Password
                                </Button>
                            ) : (
                                <div className="space-y-4 p-4 bg-white/5 rounded-lg">
                                    <div className="space-y-2">
                                        <Label>Current Password</Label>
                                        <Input
                                            type="password"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            className="bg-white/5 border-white/10 text-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>New Password</Label>
                                        <Input
                                            type="password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="bg-white/5 border-white/10 text-white"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Confirm New Password</Label>
                                        <Input
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="bg-white/5 border-white/10 text-white"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={handleChangePassword}
                                            disabled={isSaving}
                                            className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                                        >
                                            {isSaving ? 'Changing...' : 'Change Password'}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setShowPasswordForm(false);
                                                setCurrentPassword('');
                                                setNewPassword('');
                                                setConfirmPassword('');
                                            }}
                                            className="border-white/10 text-white hover:bg-white/5"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Danger Zone */}
                    <Card className="glass-card border-0 border-red-500/20">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-red-400">
                                <Trash2 className="w-5 h-5" />
                                Danger Zone
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {!showDeleteConfirm ? (
                                <Button
                                    variant="outline"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete Account
                                </Button>
                            ) : (
                                <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                                    <p className="text-white mb-4">Are you sure you want to delete your account? This action cannot be undone. All your data, devices, and threat history will be permanently deleted.</p>
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={handleDeleteAccount}
                                            className="bg-red-500 hover:bg-red-600 text-white"
                                        >
                                            Yes, Delete My Account
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => setShowDeleteConfirm(false)}
                                            className="border-white/10 text-white hover:bg-white/5"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
};

export default Profile;
